import React, { useEffect, useRef } from 'react';

/**
 * EventBadgeOverlay — Renders a fade animation between the team avatar pin
 * and the event category icon for markers with active events.
 *
 */
function EventBadgeOverlay({ google, map, position, defaultPinUrl, eventIconUrl, pinSize, mode = 'fade' }) {
    const overlayRef = useRef(null);

    useEffect(() => {
        if (!google || !map || !position) return;

        const overlay = new google.maps.OverlayView();

        overlay.onAdd = function () {
            const panes = this.getPanes();
            const div = document.createElement('div');
            div.style.position = 'absolute';

            if (mode === 'pulse') {
                // Pulse mode: single image with scale/pulse animation
                div.innerHTML = `
                    <div class="event-badge-pin" style="width: ${pinSize.width}px; height: ${pinSize.height}px;">
                        <img src="${defaultPinUrl}" class="badge-img badge-pulse" />
                    </div>
                    <style>
                        .event-badge-pin {
                            position: relative;
                            cursor: pointer;
                        }
                        .badge-img {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                        }
                        .badge-pulse {
                            animation: badge-pulse-scale 2s ease-in-out infinite;
                        }
                        @keyframes badge-pulse-scale {
                            0% { transform: scale(0.8); }
                            50% { transform: scale(1.0); }
                            100% { transform: scale(0.8); }
                        }
                    </style>
                `;
            } else {
                // Fade mode (default): two images alternating via CSS keyframes
                div.innerHTML = `
                    <div class="event-badge-pin" style="width: ${pinSize.width}px; height: ${pinSize.height}px;">
                        <img src="${defaultPinUrl}" class="badge-img badge-default" />
                        <img src="${eventIconUrl}" class="badge-img badge-event" />
                    </div>
                    <style>
                        .event-badge-pin {
                            position: relative;
                            cursor: pointer;
                        }
                        .badge-img {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                        }
                        .badge-default {
                            animation: badge-fade-default 3s ease-in-out infinite;
                        }
                        .badge-event {
                            animation: badge-fade-event 3s ease-in-out infinite;
                            opacity: 0;
                        }
                        @keyframes badge-fade-default {
                            0%, 40% { opacity: 1; }
                            50%, 90% { opacity: 0; }
                            100% { opacity: 1; }
                        }
                        @keyframes badge-fade-event {
                            0%, 40% { opacity: 0; }
                            50%, 90% { opacity: 1; }
                            100% { opacity: 0; }
                        }
                    </style>
                `;
            }

            overlayRef.current = div;
            panes.overlayLayer.appendChild(div);
        };

        overlay.draw = function () {
            if (!overlayRef.current) return;
            const projection = this.getProjection();
            const latLng = new google.maps.LatLng(position.lat, position.lng);
            const point = projection.fromLatLngToDivPixel(latLng);
            if (overlayRef.current) {
                overlayRef.current.style.left = `${point.x - pinSize.width / 2}px`;
                overlayRef.current.style.top = `${point.y - pinSize.height}px`;
            }
        };

        overlay.onRemove = function () {
            if (overlayRef.current && overlayRef.current.parentNode) {
                overlayRef.current.parentNode.removeChild(overlayRef.current);
            }
        };

        overlay.setMap(map);

        return () => {
            overlay.setMap(null);
        };
    }, [google, map, position, defaultPinUrl, eventIconUrl, pinSize, mode]);

    return null;
}

export default EventBadgeOverlay;
